import { useEffect, useState } from 'react'
import type { Documento } from '@services/db.js'
import { getFotos } from '@services/fotos.js'
import { useDocumentPreviewUrl } from '@hooks/useDocumentPreviewUrl.js'
import { Modal } from '@components/primitives/Modal.js'
import { formatDate } from '@utils/format.js'

interface Props {
  fotoIds: number[]
  /** Quando informado, mostra botão de remover em cada foto. */
  onRemover?: (fotoId: number) => void
}

/** Galeria de miniaturas de uma anotação, com visualização em tela cheia. */
export function DiarioFotos({ fotoIds, onRemover }: Props) {
  const [docs, setDocs] = useState<Documento[]>([])
  const [aberta, setAberta] = useState<Documento | null>(null)

  useEffect(() => {
    let ativo = true
    getFotos(fotoIds).then((rows) => {
      if (ativo) setDocs(rows)
    })
    return () => {
      ativo = false
    }
  }, [fotoIds])

  if (docs.length === 0) return null

  return (
    <>
      <div className='cf3-diario-galeria' aria-label={`${docs.length} mídias`}>
        {docs.map((d) => (
          <FotoThumb
            key={d.id}
            doc={d}
            onOpen={() => setAberta(d)}
            {...(onRemover ? { onRemover } : {})}
          />
        ))}
      </div>
      <FotoViewer doc={aberta} onClose={() => setAberta(null)} />
    </>
  )
}

function FotoThumb({
  doc,
  onOpen,
  onRemover,
}: {
  doc: Documento
  onOpen: () => void
  onRemover?: (fotoId: number) => void
}) {
  const url = useDocumentPreviewUrl(doc)
  return (
    <div className='cf3-diario-foto'>
      <button
        type='button'
        className='cf3-diario-galeria__thumb'
        onClick={onOpen}
        aria-label={`Abrir ${doc.tipo.startsWith('video/') ? 'vídeo' : 'foto'} ${doc.nome}`}
      >
        {url &&
          (doc.tipo.startsWith('video/') ? (
            <video src={url} muted playsInline preload='metadata' />
          ) : (
            <img src={url} alt={doc.observacoes ?? doc.nome} loading='lazy' />
          ))}
      </button>
      {onRemover && doc.id != null && (
        <button
          type='button'
          className='cf3-diario-foto__remove'
          aria-label='Remover mídia'
          onClick={() => onRemover(doc.id!)}
        >
          ×
        </button>
      )}
    </div>
  )
}

function FotoViewer({ doc, onClose }: { doc: Documento | null; onClose: () => void }) {
  const url = useDocumentPreviewUrl(doc)
  return (
    <Modal open={doc !== null} title={doc ? formatDate(doc.data) : ''} size='lg' onClose={onClose}>
      {doc && (
        <div className='cf3-foto-viewer'>
          {url &&
            (doc.tipo.startsWith('video/') ? (
              <video src={url} controls playsInline preload='metadata' />
            ) : (
              <img src={url} alt={doc.observacoes ?? doc.nome} />
            ))}
          {doc.observacoes && <p className='cf3-gestao-form__nota'>{doc.observacoes}</p>}
        </div>
      )}
    </Modal>
  )
}
